import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ToggleSwitch from '@/components/ui/ToggleSwitch/ToggleSwitch';
import Button from '@/components/ui/Button';
import { DocumentWithDetailsDtoIn } from '@/models/in/DocumentWithDetailsDtoIn';
import { DocumentStatus } from '@/models/common/DocumentStatus';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import alertIcon from '@/assets/icons/alert-circle.svg';
import styles from './RequestCompletionDialog.module.css';

interface DocumentUpdate {
  documentId: number;
  status: DocumentStatus;
  comment: string;
}

interface RequestCompletionDtoOut {
  documentUpdates: DocumentUpdate[];
  officeComment: string;
  showInPersonalArea: boolean;
  sendEmail: boolean;
  isDoneWithPendingCompletion: boolean;
  frozenAmount?: number;
}

export interface RequestCompletionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  apiEndpoint: string;
  onSuccess: () => void;
  documents: DocumentWithDetailsDtoIn[];
  showVisibilityToggle?: boolean;
  isDoneWithPendingCompletion?: boolean;
  defaultFrozenAmount?: number;
}

const RequestCompletionDialog = ({ isOpen, onClose, apiEndpoint, onSuccess, documents, showVisibilityToggle = true, isDoneWithPendingCompletion = false, defaultFrozenAmount }: RequestCompletionDialogProps) => {
  const { t } = useTranslation();
  const [selectedDocuments, setSelectedDocuments] = useState<Record<number, boolean>>({});
  const [documentComments, setDocumentComments] = useState<Record<number, string>>({});
  const [officeComment, setOfficeComment] = useState('');
  const [showInPersonalArea, setShowInPersonalArea] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [frozenAmount, setFrozenAmount] = useState<string>(String(defaultFrozenAmount ?? 0));

  const { mutate: requestCompletion, isPending } = usePostAuthApi<void, RequestCompletionDtoOut>(apiEndpoint);

  const resetForm = () => {
    setSelectedDocuments({});
    setDocumentComments({});
    setOfficeComment('');
    setShowInPersonalArea(true);
    setSendEmail(false);
    setFrozenAmount(String(defaultFrozenAmount ?? 0));
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const toggleDocument = (documentId: number) => {
    setSelectedDocuments((prev) => ({
      ...prev,
      [documentId]: !prev[documentId],
    }));
  };

  const updateComment = (documentId: number, comment: string) => {
    setDocumentComments((prev) => ({
      ...prev,
      [documentId]: comment,
    }));
  };

  const handleSubmit = () => {
    const documentUpdates: DocumentUpdate[] = Object.entries(selectedDocuments)
      .filter(([_, selected]) => selected)
      .map(([idStr]) => {
        const documentId = parseInt(idStr, 10);
        return {
          documentId,
          status: DocumentStatus.Rejected,
          comment: documentComments[documentId] || '',
        };
      });

    requestCompletion(
      {
        documentUpdates,
        officeComment,
        showInPersonalArea: showVisibilityToggle ? showInPersonalArea : sendEmail,
        sendEmail,
        isDoneWithPendingCompletion,
        frozenAmount: isDoneWithPendingCompletion ? parseFloat(frozenAmount) || 0 : undefined,
      },
      {
        onSuccess: () => {
          onSuccess();
          resetForm();
          onClose();
        },
      }
    );
  };

  const parsedFrozenAmount = parseFloat(frozenAmount);
  const canSubmit = officeComment.trim().length > 0
    && (!isDoneWithPendingCompletion || (parsedFrozenAmount > 0 && !isNaN(parsedFrozenAmount)));

  return (
    <MainDialogLayout open={isOpen} onOpenChange={(open: boolean) => !open && handleClose()} title={t('documents.requestCompletion')}>
      <div className={styles.content}>
        {isDoneWithPendingCompletion && (
          <div className={styles.freezeWarning}>
            <img src={alertIcon} alt="" />
            <div className="flex flex-col gap-1">
              <span className="body-sm-semibold">{t('freezeWarningTitle')}</span>
              <span className="body-sm-regular">{t('freezeWarningDescription')}</span>
            </div>
          </div>
        )}
        <div className={styles.section}>
          <h4 className="body-md-semibold">{t('selectDocumentsToReject')}</h4>
          <div className={styles.documentsList}>
            {documents.map((doc) => (
              <div key={doc.id} className={styles.documentItem}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selectedDocuments[doc.id] || false}
                    onChange={() => toggleDocument(doc.id)}
                  />
                  <a href={doc.uri} target="_blank" rel="noopener noreferrer" className="body-sm-link text-(--font-link)">
                    {doc.name}
                  </a>
                  <DownloadFileButton uri={doc.uri} fileName={doc.name} />
                </label>
                {selectedDocuments[doc.id] && (
                  <input
                    type="text"
                    placeholder={t('rejectionReason')}
                    value={documentComments[doc.id] || ''}
                    onChange={(e) => updateComment(doc.id, e.target.value)}
                    className={styles.commentInput}
                  />
                )}
              </div>
            ))}
            {documents.length === 0 && (
              <p className="body-sm-regular text-(--font-body-3)">{t('noDocumentsAvailable')}</p>
            )}
          </div>
        </div>
        <div className={styles.section}>
          <h4 className="body-md-semibold">{t('officeComment')} *</h4>
          <textarea
            className={styles.textarea}
            value={officeComment}
            onChange={(e) => setOfficeComment(e.target.value)}
            placeholder={t('enterOfficeComment')}
            rows={4}
          />
        </div>
        {isDoneWithPendingCompletion && (
          <div className={styles.section}>
            <h4 className="body-md-semibold">{t('frozenAmountLabel')}</h4>
            <p className="body-sm-regular text-(--font-body-3)">{t('frozenAmountDefault')}</p>
            <input
              type="number"
              min="0"
              step="any"
              className={styles.commentInput}
              value={frozenAmount}
              onChange={(e) => setFrozenAmount(e.target.value)}
            />
          </div>
        )}
        {showVisibilityToggle && (
          <ToggleSwitch
            label={t('officeComments.showInPersonalArea')}
            checked={showInPersonalArea}
            onChange={setShowInPersonalArea}
          />
        )}
        <ToggleSwitch
          label={t('sendEmailNotification')}
          checked={sendEmail}
          onChange={setSendEmail}
        />
      </div>
      <div className={styles.footer}>
        <Button variant="outline" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit || isPending}>
          {isPending ? t('sending') : t('send')}
        </Button>
      </div>
    </MainDialogLayout>
  );
};

export default RequestCompletionDialog;
